import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useBrand } from "../lib/brand";
import KatlasProofCard, { KatlasNodeBadge } from "../components/KatlasProofCard";
import CarExplainer from "../components/CarExplainer";
import { Sparkles, ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";

const SHARED_TAGLINE =
    "AI suggests. Authorised roles decide. KATLAS signs the receipt.";

export default function ResponsibleTechnology() {
    const brand = useBrand();
    const [scenario, setScenario] = useState(null);
    const [submitting, setSubmitting] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/responsible-technology/scenario")
            .then((r) => setScenario(r.data))
            .catch(() => setScenario(null));
    }, []);

    const decide = async (outcome) => {
        setSubmitting(outcome);
        try {
            const endpoint =
                outcome === "approved"
                    ? "/responsible-technology/recommendation/approve"
                    : "/responsible-technology/recommendation/refuse";
            const r = await api.post(endpoint);
            setResult(r.data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setSubmitting(null);
        }
    };

    if (!scenario)
        return (
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-slate-500">
                Loading governed action…
            </div>
        );

    return (
        <div data-testid="responsible-technology-page">
            {/* Hero */}
            <section className="border-b border-slate-200 bg-slate-50/60">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-10">
                    <div className="flex items-center gap-3 mb-5 flex-wrap">
                        <Link to="/" className="overline hover:text-slate-900">
                            ← Back
                        </Link>
                        <span className="w-8 h-px bg-slate-300" />
                        <KatlasNodeBadge />
                    </div>
                    <div className="overline mb-3">Primary governed action</div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-4xl leading-[1.08]">
                        {scenario.title}
                    </h1>
                    <p className="mt-4 text-base lg:text-lg text-slate-700 max-w-3xl leading-relaxed">
                        {scenario.summary}
                    </p>
                </div>
            </section>

            {/* Cierge baseline lines */}
            <section className="border-b border-slate-200 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {scenario.lines.map((l, idx) => (
                        <div
                            key={l}
                            className="tile rounded-sm p-5"
                            data-testid={`cierge-line-${idx}`}
                        >
                            <Sparkles size={16} strokeWidth={1.5} className="text-slate-700 mb-2" />
                            <p className="text-sm text-slate-800 leading-relaxed">{l}</p>
                        </div>
                    ))}
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left — action + result */}
                <div className="lg:col-span-2 space-y-6">
                    {!result && (
                        <section className="tile rounded-sm p-6 lg:p-8" data-testid="decision-gate">
                            <div className="overline mb-2">Authority decision</div>
                            <h3 className="text-xl lg:text-2xl font-bold tracking-tight mb-2">
                                Approve or refuse the recommendation. KATLAS will sign the receipt.
                            </h3>
                            <p className="text-sm text-slate-600 mb-6 max-w-2xl">
                                {SHARED_TAGLINE}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <button
                                    disabled={submitting !== null}
                                    onClick={() => decide("approved")}
                                    className="text-left rounded-sm border-2 border-emerald-700 text-emerald-900 hover:bg-emerald-50 px-5 py-4 transition-colors duration-200 disabled:opacity-50"
                                    data-testid="approve-btn"
                                >
                                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-1 opacity-70 inline-flex items-center gap-1">
                                        <CheckCircle2 size={12} strokeWidth={1.5} /> Approve
                                    </div>
                                    <div className="font-semibold tracking-tight">
                                        Approve governed recommendation
                                    </div>
                                </button>
                                <button
                                    disabled={submitting !== null}
                                    onClick={() => decide("refused")}
                                    className="text-left rounded-sm border-2 border-rose-700 text-rose-900 hover:bg-rose-50 px-5 py-4 transition-colors duration-200 disabled:opacity-50"
                                    data-testid="refuse-btn"
                                >
                                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-1 opacity-70 inline-flex items-center gap-1">
                                        <AlertTriangle size={12} strokeWidth={1.5} /> Refuse
                                    </div>
                                    <div className="font-semibold tracking-tight">
                                        Refuse / escalate for review
                                    </div>
                                </button>
                            </div>
                            {submitting && (
                                <p className="mt-4 text-xs font-mono text-slate-500">
                                    Signing receipt via KATLAS node…
                                </p>
                            )}
                        </section>
                    )}

                    {result && (
                        <>
                            <section className="tile rounded-sm p-6 lg:p-8" data-testid="decision-summary">
                                <div className="flex items-center gap-2 mb-3">
                                    <ShieldCheck
                                        size={18}
                                        strokeWidth={1.5}
                                        className={result.receipt?.live ? "text-emerald-600" : "text-amber-600"}
                                    />
                                    <span className="overline">Decision recorded</span>
                                </div>
                                <h2 className="text-2xl lg:text-3xl font-bold tracking-tight">
                                    {result.artifact?.outcome === "approved"
                                        ? "Approved governed recommendation"
                                        : "Refused / escalated for review"}
                                </h2>
                                <p className="text-sm text-slate-600 mt-2">
                                    Event ID:{" "}
                                    <span className="font-mono">{result.artifact?.event_id}</span>
                                </p>
                                <div className="mt-4">
                                    <a
                                        href={`/r/${result.receipt?.receipt_hash}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-ghost text-sm px-4 py-2 rounded-sm"
                                        data-testid="public-receipt-link"
                                    >
                                        Public-safe receipt view ↗
                                    </a>
                                </div>
                            </section>
                            <KatlasProofCard receipt={result.receipt} />
                            <EvidenceBoundary scenario={scenario} />
                        </>
                    )}
                </div>

                {/* Right — CAR */}
                <aside className="space-y-6">
                    <div className="tile rounded-sm p-6">
                        <div className="overline mb-3">CAR mapping</div>
                        <Mini label="Custody" value={scenario.car_mapping.custody} />
                        <Mini label="Authority" value={scenario.car_mapping.authority} />
                        <Mini label="Receipts" value={scenario.car_mapping.receipts} />
                    </div>
                    <div className="tile rounded-sm p-6">
                        <div className="overline mb-3">Roles</div>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <span className="font-mono text-[11px] text-slate-500 uppercase tracking-[0.18em]">
                                    Actor
                                </span>
                                <div className="text-slate-800">
                                    {scenario.actor_role.replace(/_/g, " ")}
                                </div>
                            </li>
                            <li>
                                <span className="font-mono text-[11px] text-slate-500 uppercase tracking-[0.18em]">
                                    Counterparty
                                </span>
                                <div className="text-slate-800">
                                    {scenario.counterparty_role.replace(/_/g, " ")}
                                </div>
                            </li>
                        </ul>
                    </div>
                </aside>
            </div>

            {!result && (
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
                    <div className="overline mb-3">Governance substrate</div>
                    <CarExplainer />
                </div>
            )}
        </div>
    );
}

function EvidenceBoundary({ scenario }) {
    return (
        <section className="tile rounded-sm p-6 lg:p-8" data-testid="evidence-boundary">
            <div className="overline mb-3">Evidence boundary</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <div className="overline text-emerald-700 mb-2">Sent to KATLAS</div>
                    <ul className="space-y-1.5 text-sm text-slate-700">
                        <li className="flex gap-2"><span className="text-slate-400">·</span> recommendation event reference</li>
                        <li className="flex gap-2"><span className="text-slate-400">·</span> role and authority metadata</li>
                        <li className="flex gap-2"><span className="text-slate-400">·</span> evidence hashes / references</li>
                        <li className="flex gap-2"><span className="text-slate-400">·</span> shared and withheld evidence counts</li>
                    </ul>
                </div>
                <div>
                    <div className="overline text-amber-700 mb-2">Withheld / internal only</div>
                    <ul className="space-y-1.5 text-sm text-slate-700">
                        {[...scenario.withheld_evidence, ...scenario.not_sent_to_node].map((it) => (
                            <li key={it} className="flex gap-2"><span className="text-slate-400">·</span> {it}</li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function Mini({ label, value }) {
    return (
        <div className="mb-3 last:mb-0">
            <div className="overline mb-1">{label}</div>
            <p className="text-sm text-slate-700 leading-relaxed">{value}</p>
        </div>
    );
}
